import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import { Recovery as Words } from '@beyond-js/ui';
import { Recovery, TechnicalDetails, useRecovery } from '@beyond-js/ui/react';
import { usePresence } from '@accounts/session';
import { Refusal } from '@accounts/transport';

/** The page's one notice, as `useRecovery` of `@beyond-js/ui` makes it: null until it is mounted */
type Notice = ReturnType<typeof useRecovery>;

/** A read of a page, as its notice knows it */
export interface Unread {
	/** One per independent read of the page */
	key: string;
	/** What it is, as a noun, said only in the notice's Technical details ("Ways to sign in") */
	title: string;
	/** Why it was not read; only Beyond Accounts not answering it joins the notice */
	unread?: Refusal;
	/** Reads it again: true when Accounts answered, a refusal included, and false when it did not */
	run: () => Promise<boolean>;
	/** Counts as not answered whatever the refusal says: a session read that was neither in nor out */
	outage?: boolean;
}

const Scope = createContext<Notice>(null);

/**
 * Try again, the last-tried line, the browser being offline ("You're offline", 0.33.0) and Technical details
 * in the interface's language; English is the package's own
 */
const labels = { en: {}, es: { ...Words.labels.es, ...TechnicalDetails.labels.es } };

/** Where focus goes when the last read answers while it was in the notice: the page's heading */
function heading(): HTMLElement | null {
	const page = globalThis.document;
	return page?.querySelector<HTMLElement>('h1.bui-page-heading') ?? page?.querySelector<HTMLElement>('h1') ?? null;
}

/** Whether Beyond Accounts did not answer the read, the one cause this notice says */
function failed({ unread, outage }: Unread): boolean {
	return Boolean(unread && (outage || unread.unreachable));
}

/** A read's row in the notice's Technical details (D43): the service's words, its request and its time */
function evidence(refusal: Refusal) {
	return { request: refusal.request ?? null, time: refusal.at, text: `${refusal.code}: ${refusal.message}` };
}

/**
 * The page's one notice while Beyond Accounts does not answer its reads (D163, `Recovery` of
 * `@beyond-js/ui` 0.24.0): one line in the warning tone on the H1's edge with who did not answer, when it
 * last tried ("Last tried 12:04 · trying again by itself") and one Try again, which reads every one of
 * them again. It lists nothing: each region says it is unavailable in its own place, and the reads'
 * request identifiers and times are its one Technical details, a row each. The reads are tried again by
 * themselves on the family's schedule (D132), and each leaves the notice when it answers.
 *
 * The page's own reads are `reads`; a region drawn inside it joins with `useUnread`. A refusal, a session
 * that ended and a change whose outcome is unknown are never here: they keep their own answers.
 *
 * @param whole Nothing on the page could be read: the notice is the page's body, in `Unavailable`'s form,
 * and the page draws no region under it
 */
export function Outage({ reads = [], whole = false, children }: { reads?: Unread[]; whole?: boolean; children?: ReactNode }) {
	const { text, language } = usePresence();
	const notice = useRecovery({ title: text('outage.accounts'), labels: labels[language], locale: language, focus: heading });
	const given = reads.filter(failed).map(read => ({ key: read.key, title: read.title, details: evidence(read.unread as Refusal), run: read.run }));
	return (
		<Scope.Provider value={notice}>
			{/* Its host takes no place in the page's grid: only the notice is laid out, and not while hidden */}
			<div className="contents [&>.bui-host]:contents">{notice ? <Recovery notice={notice} reads={given} whole={whole} /> : null}</div>
			{children}
		</Scope.Provider>
	);
}

/**
 * A region's read joins the page's `Outage` while Beyond Accounts does not answer it, updated in place on
 * each try so its schedule goes on, and leaves it when it answers, when the region is hidden or when it
 * goes. Returns whether the read did not answer, which the region says in its own place ("Not available
 * right now", or "Last updated" over what it kept), never with a cause or a Try again of its own.
 *
 * @param shown Whether the region is on the page now: a hidden view's read joins once it is shown
 */
export function useUnread(read: Unread, shown = true): boolean {
	const notice = useContext(Scope);
	const latest = useRef(read.run);
	latest.current = read.run;
	const outage = failed(read);
	const joined = shown && outage;
	const { key, title, unread } = read;

	useEffect(() => {
		if (!notice || !joined || !unread) return;
		notice.set(key, { title, details: evidence(unread), run: () => latest.current() });
	}, [notice, joined, key, title, unread]);

	// Only an answer, a hidden region or its leaving takes it out: a new try's evidence replaces its row in place
	useEffect(() => {
		if (!notice || !joined) return undefined;
		return () => notice.delete(key);
	}, [notice, joined, key]);

	return outage;
}

/**
 * One read of a page, run by `run`, which stays the same: why it got nothing (`unread`, an outage kept
 * while it reads again) and when it last answered (`at`), for the page's `Outage` and its region's place.
 * `work` is read when it runs, so it may close over the page's latest state.
 */
export function useRead(work: () => Promise<unknown>) {
	const [unread, setUnread] = useState<Refusal>();
	const [at, setAt] = useState<number>();
	const latest = useRef(work);
	latest.current = work;
	const run = useCallback(
		() =>
			Refusal.answered(async () => {
				await latest.current();
				setAt(Date.now());
			}, setUnread),
		[]
	);
	return { unread, at, run };
}
